/**
 * C-43 «Звонок» (изменение v1.14): исходящий вызов, соединение, разговор, восстановление, итог.
 *
 * По образцу Telegram (Mobbin: экран звонка с эмодзи ключа вверху, аватаром и круглыми кнопками):
 * сверху — «свернуть» и четыре эмодзи ключа шифрования (нажатие открывает объяснение C-45), в
 * центре — аватар с пульсом, имя и статус с таймером, внизу — микрофон, выход звука (где браузер
 * это умеет) и «завершить». «Назад» и `Escape` сворачивают звонок в полосу C-44, а не завершают.
 */
import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'

import { callEndText, callTimerLabel } from '@entities/call/labels'
import { callManager } from '@features/calls/manager'
import type { CallView } from '@features/calls/session'
import { useCallStore } from '@features/calls/store'
import { SAS_EMOJI } from '@shared/crypto/sas-emoji'
import { useModalLayer } from '@shared/lib/modal-layer'
import { Avatar, Icon, cx } from '@shared/ui'

import { CallKeyPanel } from './CallKeyPanel'
import { useCallSeconds } from './use-call-seconds'
import { useCallPeer } from './use-call-peer'
import styles from './call.module.css'

export function callStatusText(view: CallView, seconds: number): string {
  switch (view.phase) {
    case 'starting':
      return view.role === 'caller' ? 'Запрос…' : 'Соединение…'
    case 'ringing':
      return 'Звоним…'
    case 'connecting':
      return view.sas ? 'Соединение…' : 'Обмен ключами…'
    case 'active':
      return callTimerLabel(seconds)
    case 'reconnecting':
      return 'Восстанавливаем связь…'
    case 'ended':
      // Сбой до соединения — звонка не было, как и в записи диалога («Звонок не состоялся»).
      if (view.failure || (view.endReason === 'failed' && view.connectedAt === null))
        return 'Звонок не состоялся'
      return callEndText(view.endReason, view.role)
  }
}

interface OutputDevice {
  deviceId: string
  label: string
}

/** Выбор выхода звука есть только там, где браузер умеет `setSinkId` и выходов больше одного. */
function useOutputs(active: boolean): OutputDevice[] {
  const [outputs, setOutputs] = useState<OutputDevice[]>([])
  useEffect(() => {
    if (!active) return
    const media = globalThis.navigator?.mediaDevices
    const supported = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
    if (!media?.enumerateDevices || !supported) return
    let cancelled = false
    const load = () =>
      void media
        .enumerateDevices()
        .then((devices) => {
          if (cancelled) return
          setOutputs(
            devices
              .filter((device) => device.kind === 'audiooutput' && device.deviceId)
              .map((device, index) => ({
                deviceId: device.deviceId,
                label: device.label || `Выход ${index + 1}`,
              })),
          )
        })
        .catch(() => undefined)
    load()
    media.addEventListener?.('devicechange', load)
    return () => {
      cancelled = true
      media.removeEventListener?.('devicechange', load)
    }
  }, [active])
  return outputs
}

export function CallScreen({ view }: { view: CallView }) {
  const setMinimized = useCallStore((s) => s.setMinimized)
  const minimize = () => setMinimized(true)
  const { ref, layerRef } = useModalLayer(true, minimize)
  const peer = useCallPeer(view.conversationId)
  const seconds = useCallSeconds(view.connectedAt)
  const [keyOpen, setKeyOpen] = useState(false)
  const [tip, setTip] = useState(false)
  const [outputMenu, setOutputMenu] = useState(false)
  const [output, setOutput] = useState<string | null>(null)
  const tipShown = useRef(false)
  const live = view.phase !== 'ended'
  const outputs = useOutputs(live)
  const emoji = view.sas?.map((index) => SAS_EMOJI[index]?.emoji ?? '').join('') ?? ''
  const emojiNames = view.sas?.map((index) => SAS_EMOJI[index]?.name ?? '').join(', ') ?? ''

  // Как в Telegram: при первом появлении эмодзи — подсказка «ключ шифрования этого звонка».
  useEffect(() => {
    if (!view.sas || tipShown.current) return
    tipShown.current = true
    setTip(true)
    const timer = setTimeout(() => setTip(false), 4_000)
    return () => clearTimeout(timer)
  }, [view.sas])

  const pulsing = view.phase === 'starting' || view.phase === 'ringing' || view.phase === 'connecting'
  const status = callStatusText(view, seconds)

  return createPortal(
    <div ref={layerRef} data-modal-layer>
      <div
        ref={ref}
        className={styles['screen']}
        role="dialog"
        aria-modal="true"
        aria-label={`Звонок: ${peer.name}`}
        tabIndex={-1}
        data-call-phase={view.phase}
      >
        <div className={styles['topBar']}>
          <button
            type="button"
            className={styles['iconButton']}
            aria-label="Свернуть звонок"
            onClick={minimize}
          >
            <Icon name="chevron-down" size={24} />
          </button>
          <div className={styles['keyArea']}>
            {view.sas ? (
              <button
                type="button"
                className={styles['keyButton']}
                aria-label={`Ключ шифрования звонка: ${emojiNames}`}
                data-call-key={emoji}
                onClick={() => setKeyOpen(true)}
              >
                {emoji}
              </button>
            ) : null}
            {tip && !keyOpen ? (
              <span className={styles['keyTip']} aria-hidden="true">
                <Icon name="lock" size={14} />
                Ключ шифрования этого звонка
              </span>
            ) : null}
          </div>
          <span />
        </div>

        <div className={styles['center']}>
          <div className={cx(styles['avatarWrap'], pulsing && styles['pulsing'])}>
            <Avatar
              className={styles['avatar']}
              name={peer.name}
              photo={peer.photo}
              availability={peer.availability}
              size="2xl"
            />
          </div>
          <h1 className={styles['name']}>{peer.name}</h1>
          <p className={styles['status']} role="status" aria-live="polite" data-call-status>
            {view.phase === 'active' && view.quality === 'poor' ? <Icon name="wifi-off" size={16} /> : null}
            {status}
          </p>
          {view.sas ? null : live ? (
            <span className={styles['lockNote']}>
              <Icon name="lock" size={14} />
              Сквозное шифрование
            </span>
          ) : null}
          {view.peerMuted && live ? (
            <span className={styles['pill']}>
              <Icon name="mic-off" size={16} />У собеседника выключен микрофон
            </span>
          ) : null}
          {view.muted && live ? (
            <span className={styles['pill']}>
              <Icon name="mic-off" size={16} />
              Ваш микрофон выключен
            </span>
          ) : null}
        </div>

        {outputMenu && outputs.length > 1 ? (
          <div className={styles['outputs']} role="menu" aria-label="Выход звука">
            {outputs.map((device) => (
              <button
                key={device.deviceId}
                type="button"
                role="menuitemradio"
                aria-checked={output === device.deviceId}
                className={styles['outputItem']}
                onClick={() => {
                  setOutputMenu(false)
                  void callManager.setOutput(device.deviceId).then((ok) => {
                    if (ok) setOutput(device.deviceId)
                  })
                }}
              >
                {device.label}
                {output === device.deviceId ? <Icon name="check" size={16} /> : null}
              </button>
            ))}
          </div>
        ) : null}

        <div className={styles['controls']}>
          {outputs.length > 1 ? (
            <div className={styles['controlSlot']}>
              <button
                type="button"
                className={cx(styles['control'], outputMenu && styles['controlOn'])}
                aria-label="Выход звука"
                aria-haspopup="menu"
                aria-expanded={outputMenu}
                disabled={!live}
                onClick={() => setOutputMenu((open) => !open)}
              >
                <Icon name="volume" size={26} />
              </button>
              звук
            </div>
          ) : null}
          <div className={styles['controlSlot']}>
            <button
              type="button"
              className={cx(styles['control'], view.muted && styles['controlOn'])}
              aria-label={view.muted ? 'Включить микрофон' : 'Выключить микрофон'}
              aria-pressed={view.muted}
              disabled={!live}
              onClick={() => callManager.setMuted(!view.muted)}
            >
              <Icon name={view.muted ? 'mic-off' : 'mic'} size={26} />
            </button>
            {/* Состояние видно по кнопке и плашке «Ваш микрофон выключен» — подпись постоянная */}
            микрофон
          </div>
          <div className={styles['controlSlot']}>
            <button
              type="button"
              className={cx(styles['control'], styles['danger'])}
              aria-label="Завершить звонок"
              disabled={!live}
              onClick={() => callManager.hangup()}
            >
              <Icon name="phone" size={26} className={styles['hangupIcon']} />
            </button>
            завершить
          </div>
        </div>

        {keyOpen && view.sas ? (
          <CallKeyPanel
            sas={view.sas}
            peerName={peer.name}
            relayOnly={view.relayOnly}
            onClose={() => setKeyOpen(false)}
          />
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
